import { useCallback, useMemo } from 'react';

import { tvpil } from '../../compat/tvpil';
import { useFavoriteChannels } from '../../favorite';
import { useLastWatched } from '../../lastWatched';
import { useMostWatched } from '../../mostWatched';
import { usePersonalLineup } from '../../personalLineup/usePersonalLineup';

import { ChannelsPersonalization } from './types';

type UseChannelsPersonalizationProps = {
  referenceId: string;
  pageReferenceId?: string;
};

export function useChannelsPersonalization({ referenceId, pageReferenceId }: UseChannelsPersonalizationProps) {
  const isPersonalLineupEnabled = tvpil.rootStore.isPersonalLineupEnabled;
  const isFavoriteChannelPersonalization = tvpil.rootStore.isFavoriteChannelPersonalization;

  const {
    favoriteChannels,
    isLoading: isLoadingFavorites,
    error: favoriteError,
    refetch: refetchFavoriteChannels,
  } = useFavoriteChannels({
    enabled: isFavoriteChannelPersonalization,
  });

  const { mostWatched, isLoading: isLoadingMostWatch } = useMostWatched({
    referenceId,
    enabled: isFavoriteChannelPersonalization,
  });

  const {
    personalLineup,
    isLoading: isLoadingPersonalLineup,
    isFetching: isFetchingPersonalLineup,
    error: personalLineupError,
    refetch: refetchPersonalLineup,
  } = usePersonalLineup({
    enabled: isPersonalLineupEnabled,
  });

  const { lastWatched, isLoading: isLoadingLastWatched } = useLastWatched({
    pageReferenceId,
    referenceId,
    enabled: Boolean(pageReferenceId),
  });

  const isInitialLoading = isLoadingLastWatched || isLoadingPersonalLineup || isLoadingMostWatch || isLoadingFavorites;
  const isLoading =
    isLoadingLastWatched ||
    isLoadingPersonalLineup ||
    isFetchingPersonalLineup ||
    isLoadingMostWatch ||
    isLoadingFavorites;

  const personalization = useMemo<ChannelsPersonalization | undefined>(() => {
    if (isInitialLoading) {
      return;
    }

    if (isPersonalLineupEnabled) {
      return {
        lastWatched,
        personalLineup,
      };
    }

    if (isFavoriteChannelPersonalization) {
      return {
        favoriteChannels,
        lastWatched,
        mostWatched,
      };
    }

    return {
      lastWatched,
    };
  }, [
    favoriteChannels,
    isFavoriteChannelPersonalization,
    isInitialLoading,
    isPersonalLineupEnabled,
    lastWatched,
    mostWatched,
    personalLineup,
  ]);

  const refetch = useCallback(async () => {
    if (isFavoriteChannelPersonalization) {
      await refetchFavoriteChannels();

      return;
    }

    if (isPersonalLineupEnabled) {
      await refetchPersonalLineup();
    }
  }, [isFavoriteChannelPersonalization, isPersonalLineupEnabled, refetchFavoriteChannels, refetchPersonalLineup]);

  return {
    error: favoriteError ?? personalLineupError,
    isLoading,
    personalization,
    refetch,
  };
}
